import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useChurch } from "@/hooks/useChurch";
import { useAuth } from "@/hooks/useAuth";
import { useFreemium } from "@/hooks/useFreemium";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import TableFilterBar from "@/components/table/TableFilterBar";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, PiggyBank, TrendingUp, Scale, X } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { sortGeneralFirstByName } from "@/lib/utils";

const MONTHS_SHORT = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"] as const;

function getAnnualTotal(row: AnnualBudgetRow): number {
  return MONTHS_SHORT.reduce((sum, month) => sum + Number((row as any)[month] || 0), 0);
}

const MINISTRY_IDS_NOTE_PREFIX = "Ministry IDs: ";
const LEGACY_ORG_IDS_NOTE_PREFIX = "Organization IDs: ";

function getOrgIdsFromNotes(notes: string | null): string[] {
  if (!notes) return [];
  const lines = notes.split("\n").map((l) => l.trim());
  const idsLine = lines.find((l) =>
    l.startsWith(MINISTRY_IDS_NOTE_PREFIX) || l.startsWith(LEGACY_ORG_IDS_NOTE_PREFIX)
  );
  if (!idsLine) return [];
  return idsLine
    .replace(MINISTRY_IDS_NOTE_PREFIX, "")
    .replace(LEGACY_ORG_IDS_NOTE_PREFIX, "")
    .split(">")
    .map((id) => id.trim())
    .filter(Boolean);
}

const INCOME_CATEGORIES = ["Tithes", "Offerings", "Donations", "Other", "Custom"];
const EXPENSE_CATEGORIES_DEFAULT = ["Utilities", "Salaries", "Maintenance", "Supplies", "Missions", "Events", "Rent/Mortgage", "Insurance", "Transportation", "Other", "Custom"];

function fmtCurrencyDisplay(raw: string): string {
  if (!raw) return "";
  const clean = raw.replace(/[^0-9.]/g, "");
  const parts = clean.split(".");
  const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.length > 1 ? `${intPart}.${parts[1]}` : intPart;
}

function parseCurrencyOnChange(inputVal: string): string {
  const raw = inputVal.replace(/[^0-9.]/g, "");
  const parts = raw.split(".");
  return parts.length > 1 ? `${parts[0]}.${parts[1].slice(0, 2)}` : parts[0];
}

interface AnnualBudgetRow {
  id: string;
  church_id: string;
  year: number;
  category: string;
  record_type: string;
  organization_id: string | null;
  jan: number; feb: number; mar: number; apr: number; may: number; jun: number;
  jul: number; aug: number; sep: number; oct: number; nov: number; dec: number;
  notes: string | null;
}

type OrganizationOption = { id: string; name: string };

interface Props {
  viewYear: string;
  setViewYear: (y: string) => void;
  years: string[];
  formatCurrency: (v: number) => string;
  incomeRecords: { date: string; category: string; amount: number; record_type: string; [key: string]: any }[];
}

export default function AnnualBudget({ viewYear, setViewYear, years, formatCurrency, incomeRecords }: Props) {
  const { profile } = useChurch();
  const { user } = useAuth();
  const { isPreview, requireSubscription } = useFreemium();
  const { getFinanceOrganizationIds, hasFinanceScopedAccess, isChurchAdmin } = usePermissions();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editRow, setEditRow] = useState<AnnualBudgetRow | null>(null);
  const [filterType, setFilterType] = useState<"income" | "expense">("income");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterMinistry, setFilterMinistry] = useState("all");
  const financeScoped = !isChurchAdmin && hasFinanceScopedAccess();
  const allowedOrgIds = getFinanceOrganizationIds();

  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations", "budget_scope"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name")
        .eq("church_id", profile!.id)
        .order("name");
      if (error) throw error;
      return sortGeneralFirstByName((data || []) as OrganizationOption[]);
    },
    enabled: !!profile?.id,
  });

  const sortedOrganizations = useMemo(() => sortGeneralFirstByName(organizations), [organizations]);

  const { data: budgets = [], isLoading } = useQuery({
    queryKey: ["annual_budgets", profile?.id, viewYear],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("annual_budgets")
        .select("*")
        .eq("church_id", profile!.id)
        .eq("year", Number(viewYear))
        .order("record_type")
        .order("category");
      if (error) throw error;
      return data as AnnualBudgetRow[];
    },
    enabled: !!profile?.id,
  });

  const { data: expenseCategories = [] } = useQuery({
    queryKey: ["expense_categories", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("expense_categories")
        .select("name")
        .eq("church_id", profile!.id);
      if (error) throw error;
      return data.map((d: any) => d.name as string);
    },
    enabled: !!profile?.id,
  });

  const allExpenseCategories = useMemo(() => {
    const merged = [...new Set([...EXPENSE_CATEGORIES_DEFAULT, ...expenseCategories])];
    const sorted = merged
      .filter((c) => c !== "Custom" && c !== "Other")
      .sort();
    const hasOther = merged.includes("Other");
    return [...sorted, ...(hasOther ? ["Other"] : []), "Custom"];
  }, [expenseCategories]);

  const scopedBudgets = useMemo(() => {
    if (!financeScoped) return budgets;
    return budgets.filter((b) => b.organization_id && allowedOrgIds.includes(b.organization_id));
  }, [budgets, financeScoped, allowedOrgIds]);

  const filteredBudgets = useMemo(() => {
    let list = scopedBudgets.filter((b) => b.record_type === filterType);
    if (filterMinistry !== "all") {
      if (filterMinistry === "__none__") {
        list = list.filter((b) => !b.organization_id);
      } else {
        list = list.filter((b) => b.organization_id === filterMinistry);
      }
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((b) => b.category.toLowerCase().includes(q));
    }
    return list;
  }, [scopedBudgets, filterType, filterMinistry, searchQuery]);

  // Build actuals map — only records whose date belongs to the selected year
  const actuals = useMemo(() => {
    const map: Record<string, number> = {};
    incomeRecords
      .filter((r) => r.date.startsWith(viewYear))
      .forEach((r) => {
        const type = r.record_type || "income";
        const key = `${type}:${r.category}`;
        map[key] = (map[key] || 0) + Number(r.amount);
      });
    return map;
  }, [incomeRecords, viewYear]);

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("annual_budgets").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["annual_budgets"] });
      
    },
  });

  const totalBudgeted = filteredBudgets.reduce((sum, b) => sum + getAnnualTotal(b), 0);

  const totalActual = useMemo(() => {
    return incomeRecords
      .filter((r) => r.date.startsWith(viewYear))
      .filter((r) => (r.record_type || "income") === filterType)
      .filter((r) => {
        if (filterMinistry === "all") return true;
        const orgIds = getOrgIdsFromNotes(r.notes);
        if (filterMinistry === "__none__") return orgIds.length === 0;
        return orgIds.includes(filterMinistry);
      })
      .reduce((sum, r) => sum + Number(r.amount), 0);
  }, [incomeRecords, viewYear, filterType, filterMinistry]);

  const scopedOrganizations = useMemo(
    () => (financeScoped ? sortedOrganizations.filter((o) => allowedOrgIds.includes(o.id)) : sortedOrganizations),
    [financeScoped, sortedOrganizations, allowedOrgIds]
  );

  const selectedMinistryName = useMemo(() => {
    if (filterMinistry === "all") return "All Ministries";
    const match = scopedOrganizations.find((o) => o.id === filterMinistry);
    return match?.name || "All Ministries";
  }, [filterMinistry, scopedOrganizations]);

  const filteredContextLabel = useMemo(
    () => `${viewYear} ${selectedMinistryName} ${filterType === "expense" ? "Expense" : "Income"}`,
    [filterType, selectedMinistryName, viewYear]
  );

  const budgetFilterGroups = useMemo(
    () => [
      {
        key: "record_type",
        label: "Type",
        options: ["Income", "Expense"],
        selected: new Set([filterType === "expense" ? "Expense" : "Income"]),
        onToggle: (value: string) => setFilterType(value.toLowerCase() === "expense" ? "expense" : "income"),
        onClear: () => setFilterType("income"),
      },
      {
        key: "ministry",
        label: "Ministry",
        options: scopedOrganizations.map((o) => o.name),
        selected: filterMinistry === "all" ? new Set<string>() : new Set([selectedMinistryName]),
        onToggle: (value: string) => {
          const match = scopedOrganizations.find((o) => o.name === value);
          setFilterMinistry(match?.id || "all");
        },
        onClear: () => setFilterMinistry("all"),
      },
      {
        key: "year",
        label: "Year",
        options: years,
        selected: new Set([viewYear]),
        onToggle: (value: string) => setViewYear(value),
        onClear: () => setViewYear(years[0] || viewYear),
      },
    ],
    [filterType, scopedOrganizations, selectedMinistryName, years, viewYear, setViewYear]
  );

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="rounded-3xl border border-[#d7e4f5] bg-gradient-to-br from-white to-[#f4f8ff] p-4 sm:p-5 shadow-[0_8px_30px_-8px_rgba(2,6,23,0.10)]">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
            <CardContent className="pt-5 pb-5 px-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-muted-foreground">Total Budgeted</p>
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                  <PiggyBank className="w-4.5 h-4.5 text-primary" />
                </div>
              </div>
              <p className="text-2xl font-bold font-heading text-foreground tracking-tight">{formatCurrency(totalBudgeted)}</p>
              <p className="text-xs text-muted-foreground mt-1">{filteredContextLabel}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
            <CardContent className="pt-5 pb-5 px-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-muted-foreground">Total Actual</p>
                <div className="w-9 h-9 rounded-lg bg-success/10 flex items-center justify-center">
                  <TrendingUp className="w-4.5 h-4.5 text-success" />
                </div>
              </div>
              <p className="text-2xl font-bold font-heading text-foreground tracking-tight">{formatCurrency(totalActual)}</p>
              <p className="text-xs text-muted-foreground mt-1">Total Spent</p>
            </CardContent>
          </Card>
          {(() => {
            const variance = totalBudgeted - totalActual;
            const isGood = filterType === "expense" ? totalActual <= totalBudgeted : totalActual >= totalBudgeted;
            const displayedVariance = filterType === "expense" ? variance : -variance;
            const varianceSign = displayedVariance > 0 ? "+" : displayedVariance < 0 ? "-" : "";
            const varianceStatusText = (() => {
              if (filterType === "expense") {
                if (totalActual > totalBudgeted) return "Over Budget";
                if (totalActual < totalBudgeted) return "Under Budget";
                return "On budget";
              }
              if (totalActual > totalBudgeted) return "Exceeding Target";
              if (totalActual < totalBudgeted) return "Below Target";
              return "On target";
            })();
            return (
              <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
                <CardContent className="pt-5 pb-5 px-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-muted-foreground">Variance</p>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isGood ? "bg-success/10" : "bg-destructive/10"}`}>
                      <Scale className={`w-4.5 h-4.5 ${isGood ? "text-success" : "text-destructive"}`} />
                    </div>
                  </div>
                  <p className={`text-2xl font-bold font-heading tracking-tight ${isGood ? "text-success" : "text-destructive"}`}>
                    {varianceSign}{formatCurrency(Math.abs(displayedVariance))}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{varianceStatusText}</p>
                </CardContent>
              </Card>
            );
          })()}
        </div>
      </div>

      <div className="stat-card p-0 overflow-hidden">
        <div className="px-3 py-3 border-b border-border/40">
          <TableFilterBar
            search={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search budget..."
            searchFirstOnMobile
            mobileControlsFirst
            compactDesktopFilters
            filterColumns={2}
            filterGroups={budgetFilterGroups}
            extraControls={
              <Button
                size="sm"
                className="h-7 w-7 p-0 text-[11px] sm:h-9 sm:w-auto sm:px-3 sm:text-sm shrink-0"
                onClick={() => {
                  if (!requireSubscription()) return;
                  setEditRow(null);
                  setShowForm(true);
                }}
              >
                {isPreview && <span className="mr-1"></span>}
                <Plus className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Add Budget Line</span>
              </Button>
            }
          />
        </div>

      <div className="min-h-[420px]">
      {isLoading ? (
        <div className="flex min-h-[420px] items-center justify-center text-muted-foreground">Loading...</div>
      ) : filteredBudgets.length === 0 ? (
        <div className="flex min-h-[420px] items-center justify-center text-center px-4">
          <p className="text-muted-foreground">No budget lines for {viewYear}. Add your first budget line!</p>
        </div>
      ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-fixed min-w-[520px]">
              <thead>
                <tr className="border-b border-border bg-secondary/30">
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase px-3 py-3 w-[40%] sm:w-[50%]">Category</th>
                  <th className="text-right text-xs font-medium text-muted-foreground uppercase px-2 py-3 w-[18%]">Budget</th>
                  <th className="text-right text-xs font-medium text-muted-foreground uppercase px-2 py-3 w-[16%] sm:w-[24%]">Actual</th>
                  <th className="hidden sm:table-cell px-2 py-3 w-[0%] sm:w-[0%]"></th>
                  <th className="px-2 py-3 w-[8%]"></th>
                </tr>
              </thead>
              <tbody>
                {filteredBudgets.map((b) => {
                  const rowTotal = getAnnualTotal(b);
                  const key = `${b.record_type}:${b.category}`;
                  const actualTotal = actuals[key] || 0;
                  const pct = rowTotal > 0 ? Math.min((actualTotal / rowTotal) * 100, 100) : 0;
                  const orgName = b.organization_id ? organizations.find((o) => o.id === b.organization_id)?.name : null;

                  return (
                    <tr key={b.id} className="border-b border-border/50 hover:bg-primary/10 transition-colors">
                      <td className="px-3 py-2">
                        <div className="flex flex-col gap-0.5">
                          <span className="font-medium text-foreground truncate">{b.category}</span>
                          <span className={`text-[10px] font-medium uppercase tracking-wide ${b.record_type === "income" ? "text-success" : "text-destructive"}`}>
                            {b.record_type}
                          </span>
                          {orgName && <span className="text-[10px] text-muted-foreground truncate">{orgName}</span>}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-right text-xs font-semibold text-foreground">{formatCurrency(rowTotal)}</td>
                      <td className="px-2 py-2 text-right">
                        {(() => {
                          const isGood = b.record_type === "expense" ? actualTotal <= rowTotal : actualTotal >= rowTotal;
                          return (
                            <span className={`text-xs font-medium ${actualTotal === 0 ? "text-muted-foreground" : isGood ? "text-success" : "text-destructive"}`}>
                              {formatCurrency(actualTotal)}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="hidden sm:table-cell" />
                      <td className="px-1 py-2 w-[18%] sm:hidden">
                        <div title={`${pct.toFixed(1)}% of budget`}>
                          <Progress value={pct} className="h-1.5 cursor-default" />
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div className="flex items-center gap-1">
                          <button
                            className="p-1 rounded hover:bg-secondary"
                            onClick={() => {
                              if (!requireSubscription()) return;
                              setEditRow(b);
                              setShowForm(true);
                            }}
                          >
                            <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                          </button>
                          <button
                            className="p-1 rounded hover:bg-destructive/10"
                            onClick={() => {
                              if (!requireSubscription()) return;
                              deleteMutation.mutate(b.id);
                            }}
                          >
                            <Trash2 className="w-3.5 h-3.5 text-destructive" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border bg-secondary/40">
                  <td className="px-3 py-2.5 text-xs font-bold text-foreground uppercase tracking-wide">
                    {filterType === "income" ? "Total Income" : "Total Expenses"}
                  </td>
                  <td className="px-2 py-2.5 text-right text-xs font-bold text-foreground">{formatCurrency(totalBudgeted)}</td>
                  <td className="px-2 py-2.5 text-right">
                    {(() => {
                      const isGood = filterType === "expense" ? totalActual <= totalBudgeted : totalActual >= totalBudgeted;
                      return (
                        <span className={`text-xs font-bold ${totalActual === 0 ? "text-muted-foreground" : isGood ? "text-success" : "text-destructive"}`}>
                          {formatCurrency(totalActual)}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="hidden sm:table-cell" />
                  <td className="sm:hidden" />
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
      )}
          </div>
      </div>

      <BudgetLineForm
        open={showForm}
        onClose={() => { setShowForm(false); setEditRow(null); }}
        onSaved={(type, selectedYear) => {
          setFilterType(type as "income" | "expense");
          setViewYear(String(selectedYear));
        }}
        defaultType={filterType}
        editRow={editRow}
        year={Number(viewYear)}
        churchId={profile?.id || ""}
        userId={user?.id || ""}
        incomeCategories={INCOME_CATEGORIES}
        expenseCategories={allExpenseCategories}
        organizations={sortedOrganizations}
        scopedOrgIds={financeScoped ? allowedOrgIds : null}
      />

    </div>
  );
}

function BudgetLineForm({
  open, onClose, onSaved, defaultType, editRow, year, churchId, userId, incomeCategories, expenseCategories, organizations, scopedOrgIds,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: (type: string, year: number) => void;
  defaultType: string;
  editRow: AnnualBudgetRow | null;
  year: number;
  churchId: string;
  userId: string;
  incomeCategories: string[];
  expenseCategories: string[];
  organizations: OrganizationOption[];
  scopedOrgIds: string[] | null;
}) {
  const queryClient = useQueryClient();
  const [selectedYear, setSelectedYear] = useState<string>(() => {
    if (editRow?.year) return String(editRow.year);
    const currentYear = new Date().getFullYear();
    return String(Math.max(year, currentYear));
  });
  const [recordType, setRecordType] = useState(editRow?.record_type || defaultType);
  const [organizationId, setOrganizationId] = useState<string>(editRow?.organization_id || "");
  const [categoryOption, setCategoryOption] = useState(() => {
    const cat = editRow?.category || "";
    return (cat && !INCOME_CATEGORIES.concat(EXPENSE_CATEGORIES_DEFAULT).includes(cat)) ? "Custom" : cat;
  });
  const [customCategoryText, setCustomCategoryText] = useState(() => {
    const cat = editRow?.category || "";
    return (cat && !INCOME_CATEGORIES.concat(EXPENSE_CATEGORIES_DEFAULT).includes(cat)) ? cat : "";
  });
  const category = categoryOption === "Custom" ? customCategoryText : categoryOption;
  const [totalAmount, setTotalAmount] = useState(() => (editRow ? String(getAnnualTotal(editRow)) : ""));

  // Fetch saved custom categories for this church
  const { data: savedCustomCats = [] } = useQuery({
    queryKey: ["budget_custom_cats", churchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("annual_budgets")
        .select("category, record_type")
        .eq("church_id", churchId);
      if (error) throw error;
      return (data || []) as { category: string; record_type: string }[];
    },
    enabled: !!churchId,
  });

  const baseIncome = new Set(INCOME_CATEGORIES.filter((c) => c !== "Custom"));
  const baseExpense = new Set(EXPENSE_CATEGORIES_DEFAULT.filter((c) => c !== "Custom"));

  const customIncomeCats = useMemo(() => [
    ...new Set(savedCustomCats.filter((b) => b.record_type === "income" && !baseIncome.has(b.category)).map((b) => b.category)),
  ], [savedCustomCats]);

  const customExpenseCats = useMemo(() => [
    ...new Set(savedCustomCats.filter((b) => b.record_type === "expense" && !baseExpense.has(b.category)).map((b) => b.category)),
  ], [savedCustomCats]);

  const activeCustomCats = recordType === "income" ? customIncomeCats : customExpenseCats;

  const [editingCat, setEditingCat] = useState<string | null>(null);
  const [editCatText, setEditCatText] = useState("");
  const [deleteBlockedMessage, setDeleteBlockedMessage] = useState<string | null>(null);

  const renameCategoryMutation = useMutation({
    mutationFn: async ({ oldName, newName }: { oldName: string; newName: string }) => {
      const { error } = await supabase
        .from("annual_budgets")
        .update({ category: newName.trim() })
        .eq("church_id", churchId)
        .eq("category", oldName);
      if (error) throw error;
    },
    onSuccess: (_, { oldName, newName }) => {
      queryClient.invalidateQueries({ queryKey: ["annual_budgets"] });
      queryClient.invalidateQueries({ queryKey: ["budget_custom_cats", churchId] });
      queryClient.invalidateQueries({ queryKey: ["budget_categories"] });
      
      if (categoryOption === oldName) setCategoryOption(newName.trim());
      setEditingCat(null);
      setEditCatText("");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: async (cat: string) => {
      const { count, error: usageError } = await supabase
        .from("finance_records")
        .select("id", { count: "exact", head: true })
        .eq("church_id", churchId)
        .eq("record_type", recordType)
        .eq("category", cat);
      if (usageError) throw usageError;
      if ((count || 0) > 0) {
        throw new Error(`Cannot delete "${cat}" because it is already used in transaction records.`);
      }

      const { error } = await supabase.from("annual_budgets").delete().eq("church_id", churchId).eq("category", cat);
      if (error) throw error;
    },
    onSuccess: (_, cat) => {
      queryClient.invalidateQueries({ queryKey: ["annual_budgets"] });
      queryClient.invalidateQueries({ queryKey: ["budget_custom_cats", churchId] });
      queryClient.invalidateQueries({ queryKey: ["budget_categories"] });
      
      if (categoryOption === cat) setCategoryOption("");
    },
    onError: (err: Error) => {
      if (err.message.startsWith("Cannot delete \"")) {
        setDeleteBlockedMessage(err.message);
        return;
      }
      toast.error(err.message);
    },
  });

  // Reset form whenever the dialog opens or the row being edited changes
  useEffect(() => {
    if (!open) return;
    if (editRow) {
      setSelectedYear(String(editRow.year));
      setRecordType(editRow.record_type);
      const known = INCOME_CATEGORIES.concat(EXPENSE_CATEGORIES_DEFAULT);
      if (known.includes(editRow.category)) {
        setCategoryOption(editRow.category);
        setCustomCategoryText("");
      } else {
        // Could be a saved custom category — treat as selected directly
        setCategoryOption(editRow.category);
        setCustomCategoryText("");
      }
      setTotalAmount(String(getAnnualTotal(editRow)));
      setOrganizationId(editRow.organization_id || "");
    } else {
      const currentYear = new Date().getFullYear();
      setSelectedYear(String(Math.max(year, currentYear)));
      setRecordType(defaultType);
      setCategoryOption("");
      setCustomCategoryText("");
      setTotalAmount("");
      setOrganizationId(scopedOrgIds?.[0] || "");
    }
  }, [open, editRow, year, defaultType, scopedOrgIds]);

  const categories = recordType === "income" ? incomeCategories : expenseCategories;

  const mutation = useMutation({
    mutationFn: async () => {
      const selectedYearNum = Number(selectedYear);
      if (!Number.isInteger(selectedYearNum) || selectedYearNum < 1900 || selectedYearNum > 9999) {
        throw new Error("Please enter a valid 4-digit year.");
      }

      const normalizedCategory = category.trim();
      const payload: any = {
        church_id: churchId,
        year: selectedYearNum,
        category: normalizedCategory,
        record_type: recordType,
        organization_id: organizationId || null,
        created_by: userId || null,
      };
      const annualAmount = parseFloat(totalAmount) || 0;
      payload.jan = annualAmount;
      payload.feb = 0;
      payload.mar = 0;
      payload.apr = 0;
      payload.may = 0;
      payload.jun = 0;
      payload.jul = 0;
      payload.aug = 0;
      payload.sep = 0;
      payload.oct = 0;
      payload.nov = 0;
      payload.dec = 0;

      let existingQuery = supabase
        .from("annual_budgets")
        .select("id")
        .eq("church_id", churchId)
        .eq("year", selectedYearNum)
        .eq("category", normalizedCategory)
        .eq("record_type", recordType);

      existingQuery = organizationId
        ? existingQuery.eq("organization_id", organizationId)
        : existingQuery.is("organization_id", null);

      const { data: existingRow, error: existingError } = await existingQuery.maybeSingle();
      if (existingError) throw existingError;

      if (editRow) {
        if (existingRow && existingRow.id !== editRow.id) {
          const { error: updateExistingError } = await supabase
            .from("annual_budgets")
            .update(payload)
            .eq("id", existingRow.id);
          if (updateExistingError) throw updateExistingError;

          const { error: deleteOldError } = await supabase
            .from("annual_budgets")
            .delete()
            .eq("id", editRow.id);
          if (deleteOldError) throw deleteOldError;
        } else {
          const { error } = await supabase.from("annual_budgets").update(payload).eq("id", editRow.id);
          if (error) throw error;
        }
      } else {
        if (existingRow) {
          const { error: updateExistingError } = await supabase
            .from("annual_budgets")
            .update(payload)
            .eq("id", existingRow.id);
          if (updateExistingError) throw updateExistingError;
        } else {
          const { data: inserted, error } = await supabase
            .from("annual_budgets")
            .insert(payload)
            .select()
            .single();
          if (error) throw error;
          if (!inserted) throw new Error("Save failed — check that you have admin or staff permissions.");
        }
      }
    },
    onSuccess: () => {
      const savedYear = Number(selectedYear);
      queryClient.invalidateQueries({ queryKey: ["annual_budgets"] });
      queryClient.invalidateQueries({ queryKey: ["budget_custom_cats", churchId] });
      queryClient.invalidateQueries({ queryKey: ["budget_categories"] });
      
      onSaved(recordType, savedYear);
      onClose();
    },
    onError: (err: any) => {
      if (err?.code === "23505") {
        toast.error("A budget line with this Type, Category, Ministry, and Year already exists.");
        return;
      }
      toast.error(err?.message || "Failed to save budget line");
    },
  });

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-heading">{editRow ? "Edit Budget Line" : "Add Budget Line"}</SheetTitle>
        </SheetHeader>
        <div className="space-y-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={recordType} onValueChange={(v) => { setRecordType(v); setCategoryOption(""); setCustomCategoryText(""); }}>
                <SelectTrigger
                  className={
                    recordType === "expense"
                      ? "border-destructive/40 bg-destructive/5 text-destructive"
                      : "border-success/40 bg-success/5 text-success"
                  }
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Year</Label>
              <Input
                type="number"
                inputMode="numeric"
                min={1900}
                max={9999}
                step={1}
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
                placeholder="e.g. 2026"
              />
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <div className="flex items-center gap-1.5">
                <Select value={categoryOption} onValueChange={(v) => { setCategoryOption(v); if (v !== "Custom") setCustomCategoryText(""); }}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {categories.filter((c) => c !== "Custom").map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    {activeCustomCats.filter((c) => !categories.includes(c)).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    <SelectItem value="Custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
                {categoryOption && activeCustomCats.includes(categoryOption) && editingCat !== categoryOption && (
                  <>
                    <button
                      type="button"
                      className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground"
                      onClick={() => { setEditingCat(categoryOption); setEditCatText(categoryOption); }}
                      title="Edit category"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                      onClick={() => deleteCategoryMutation.mutate(categoryOption)}
                      title="Delete category"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
              {editingCat && editingCat === categoryOption && (
                <div className="flex items-center gap-1.5">
                  <Input
                    className="flex-1"
                    value={editCatText}
                    onChange={(e) => setEditCatText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && editCatText.trim() && editCatText.trim() !== editingCat) {
                        renameCategoryMutation.mutate({ oldName: editingCat, newName: editCatText });
                      }
                      if (e.key === "Escape") { setEditingCat(null); setEditCatText(""); }
                    }}
                    placeholder="Rename category"
                    autoFocus
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-9 px-2.5"
                    disabled={!editCatText.trim() || editCatText.trim() === editingCat}
                    onClick={() => renameCategoryMutation.mutate({ oldName: editingCat, newName: editCatText })}
                  >Save</Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-9 px-2"
                    onClick={() => { setEditingCat(null); setEditCatText(""); }}
                  ><X className="w-4 h-4" /></Button>
                </div>
              )}
              {categoryOption === "Custom" && (
                <Input
                  placeholder="Enter custom category"
                  value={customCategoryText}
                  onChange={(e) => setCustomCategoryText(e.target.value)}
                  autoFocus
                />
              )}
            </div>
          </div>

          {/* Ministry selector */}
          {(() => {
            const availableOrgs = scopedOrgIds
              ? organizations.filter((o) => scopedOrgIds.includes(o.id))
              : organizations;
            return availableOrgs.length > 0 ? (
              <div className="space-y-2">
                <Label>Ministry</Label>
                <Select value={organizationId} onValueChange={setOrganizationId}>
                  <SelectTrigger><SelectValue placeholder="Select ministry" /></SelectTrigger>
                  <SelectContent>
                    {availableOrgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : null;
          })()}

          <div className="space-y-1.5">
            <Label className="text-sm">Annual Budget Amount</Label>
            <div className="relative">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm select-none">$</span>
              <Input
                type="text"
                inputMode="numeric"
                placeholder="0"
                value={fmtCurrencyDisplay(totalAmount)}
                onChange={(e) => setTotalAmount(parseCurrencyOnChange(e.target.value))}
                className="pl-6"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              onClick={() => mutation.mutateAsync()}
              disabled={!category || parseFloat(totalAmount) <= 0 || (categoryOption === "Custom" && !customCategoryText.trim()) || mutation.isPending}
            >
              {mutation.isPending ? "Saving..." : editRow ? "Update" : "Add"}
            </Button>
          </div>
        </div>
      </SheetContent>

      <AlertDialog open={!!deleteBlockedMessage} onOpenChange={(open) => { if (!open) setDeleteBlockedMessage(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cannot Delete Category</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteBlockedMessage || "This category is currently in use and cannot be deleted."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setDeleteBlockedMessage(null)}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
